/**
 * Should an auto-run request open the Run scraper modal yet?
 *
 * Pulled out of the component because it is a decision about a RACE, and a race
 * buried in a `useEffect` is a race nobody can test. The request arrives from
 * the extractor config screen's Open action and lands on a listing that is
 * still loading two independent things — the scraper catalogue and the brand
 * override map — either of which can settle first.
 *
 * The distinction that matters is `wait` versus the two `decline` answers.
 * Waiting is not a decision; it just means come back next render. Declining is
 * final, and must still consume the request: the flag lives on the dashboard,
 * so one left armed fires on whatever brand the operator opens next.
 */
export type AutoRunDecision =
  /** Nothing asked for. */
  | 'idle'
  /** Still loading. Re-evaluate on the next render; do not consume. */
  | 'wait'
  /** Open the modal, and consume. */
  | 'run'
  /** The extractor-jobs feature flag is off. Consume, and say so. */
  | 'decline-disabled'
  /** No module resolves to this brand. Consume, and say so. */
  | 'decline-no-module';

export function autoRunDecision(input: {
  /** A run was requested and has not been consumed yet. */
  requested: boolean;
  /** `tools.extractorJobs`. It does NOT gate the config screen the request
   *  came from, so an operator can ask for a run the flag forbids. */
  flagEnabled: boolean;
  /** The scraper catalogue has resolved (`[]` counts — an unreachable
   *  extractor is an answer, not a pending state). */
  catalogueLoaded: boolean;
  /** The override map has been read. `{}` is both the initial value and a
   *  legitimate result, so this cannot be inferred from the map itself. */
  overridesLoaded: boolean;
  /** `canRunScraper(...)` — only meaningful once BOTH loads above are in. */
  canRun: boolean;
}): AutoRunDecision {
  if (!input.requested) return 'idle';
  // Checked before the loads: the flag is known synchronously, and a disabled
  // tool should say so immediately rather than after two network round-trips.
  if (!input.flagEnabled) return 'decline-disabled';
  if (!input.catalogueLoaded || !input.overridesLoaded) return 'wait';
  return input.canRun ? 'run' : 'decline-no-module';
}
